/**
 * JSON-LD Structured Data schemas for SEO.
 * Pass these to the SEO component's `schema` prop.
 */

const COSTCO_HOSTNAMES = ['costco.manageyourloans.com.au', 'costco.localhost'];

function isCostco() {
  if (typeof window === 'undefined') return false;
  return COSTCO_HOSTNAMES.some((h) => window.location.hostname === h);
}

function getBaseUrl() {
  return isCostco()
    ? 'https://costco.manageyourloans.com.au'
    : 'https://manageyourloans.com.au';
}

export function organizationSchema() {
  const baseUrl = getBaseUrl();
  const costco = isCostco();

  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: costco ? 'Manage Your Loans — Costco Partner' : 'Manage Your Loans',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: costco
      ? 'Exclusive loan solutions for Costco Executive Members. Earn Costco Shop Cards on settled loans.'
      : 'Award-winning mortgage brokers empowering Australians with tailored loan solutions.',
    telephone: '0468 007 056',
    email: 'admin@manageyourloans.com.au',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Level 1, 18-20 Knuckey St',
      addressLocality: 'Darwin',
      addressRegion: 'NT',
      postalCode: '0800',
      addressCountry: 'AU',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Australia',
    },
    sameAs: [
      'https://www.linkedin.com/company/manage-your-loans/',
      'https://www.facebook.com/manageyourloans.au',
      'https://www.instagram.com/manageyourloans.au/',
    ],
    openingHours: 'Mo-Fr 09:00-17:00',
  };
}

// Cities where MYL has an office (Andy-confirmed 2026-10-07). Every other
// location page serves the area remotely, so its schema must not imply a
// local business address.
const OFFICE_CITIES = ['darwin', 'melbourne', 'brisbane', 'sydney'];

export function localBusinessSchema(city, state, description, slug = city.toLowerCase()) {
  const url = `${getBaseUrl()}/locations/${slug}`;
  const parentOrganization = {
    '@type': 'FinancialService',
    name: 'Manage Your Loans',
    url: 'https://manageyourloans.com.au',
  };

  if (!OFFICE_CITIES.includes(slug)) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `Mortgage broking in ${city}`,
      serviceType: 'Mortgage broking',
      url,
      description,
      provider: { ...parentOrganization, telephone: '0468 007 056' },
      areaServed: { '@type': 'City', name: city },
    };
  }

  const address = {
    '@type': 'PostalAddress',
    addressLocality: city,
    addressRegion: state,
    addressCountry: 'AU',
  };
  if (slug === 'darwin') {
    address.streetAddress = 'Level 1, 18-20 Knuckey St';
    address.postalCode = '0800';
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: `Manage Your Loans — ${city}`,
    url,
    description,
    telephone: '0468 007 056',
    email: 'admin@manageyourloans.com.au',
    address,
    areaServed: {
      '@type': 'City',
      name: city,
    },
    parentOrganization,
  };
}

export function serviceSchema(name, description, path) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name,
    description,
    url: `${getBaseUrl()}${path}`,
    provider: {
      '@type': 'FinancialService',
      name: 'Manage Your Loans',
      url: 'https://manageyourloans.com.au',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Australia',
    },
  };
}

export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items) {
  // EVERY item MUST have a `url` — including the current/last page (point it at its
  // own path). A missing url drops the `item` field, which Google Search Console
  // flags as a critical "Missing field item (in itemListElement)" error and blocks
  // the breadcrumb rich result. (Bit costco.* on 2026-07-22.)
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${getBaseUrl()}${item.url}` : undefined,
    })),
  };
}
